<template>
  <div class="exam-creation">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>出题管理</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-plus" @click="showCreateExamDialog">创建考试</el-button>
        <el-button type="success" icon="el-icon-refresh" @click="loadExams">刷新</el-button>
        <el-button type="warning" icon="el-icon-sync" @click="syncPaperCounts" :loading="syncing">同步试卷套数</el-button>
      </div>
    </div>

    <!-- 考试列表 -->
    <div class="exam-list" v-loading="loading">
      <div v-if="exams.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>暂无考试，请创建您的第一个考试</p>
      </div>

      <el-table v-else :data="exams" border stripe style="width: 100%">
        <el-table-column prop="examName" label="考试名称" min-width="150">
          <template slot-scope="scope">
            <span class="exam-name" @click="openExamDetail(scope.row)">{{ scope.row.examName }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="examDescription" label="考试描述" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.examDescription || '暂无描述' }}
          </template>
        </el-table-column>

        <el-table-column prop="examType" label="考试类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.examType === 1 ? 'primary' : 'success'">
              {{ scope.row.examType === 1 ? '手动组卷' : '随机组卷' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="totalScore" label="总分" width="80" align="center">
          <template slot-scope="scope">
            {{ scope.row.totalScore }} 分
          </template>
        </el-table-column>

        <el-table-column prop="examDuration" label="时长" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.examDuration }} 分钟
          </template>
        </el-table-column>

        <el-table-column prop="paperCount" label="试卷套数" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.paperCount }} 套
          </template>
        </el-table-column>

        <el-table-column prop="startTime" label="开始时间" width="160" align="center">
          <template slot-scope="scope">
            {{ formatTime(scope.row.startTime) }}
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusTagType(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-view" @click="openExamDetail(scope.row)" title="查看详情"></el-button>
            <el-button type="text" icon="el-icon-edit" @click="editExam(scope.row)" title="编辑"></el-button>
            <el-button
              v-if="scope.row.isArchived === 0"
              type="text"
              icon="el-icon-folder-add"
              @click="archiveExam(scope.row)"
              title="归档"
              style="color: #409eff;">
            </el-button>
            <el-button
              v-if="scope.row.isArchived === 1"
              type="text"
              icon="el-icon-folder-remove"
              @click="unarchiveExam(scope.row)"
              title="取消归档"
              style="color: #67c23a;">
            </el-button>
            <el-button type="text" icon="el-icon-delete" @click="deleteExam(scope.row)" title="删除" style="color: #f56c6c;"></el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 创建/编辑考试对话框 -->
    <el-dialog
      :title="isEdit ? '编辑考试' : '创建考试'"
      :visible.sync="examDialogVisible"
      width="800px"
      @close="resetExamForm"
    >
      <el-form :model="examForm" :rules="examRules" ref="examForm" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="考试名称" prop="examName">
              <el-input v-model="examForm.examName" placeholder="请输入考试名称"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="考试类型" prop="examType">
              <el-select v-model="examForm.examType" placeholder="请选择考试类型">
                <el-option label="手动组卷" :value="1"></el-option>
                <el-option label="随机组卷" :value="2"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="考试描述" prop="examDescription">
          <el-input
            type="textarea"
            v-model="examForm.examDescription"
            placeholder="请输入考试描述"
            :rows="3"
          ></el-input>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="总分" prop="totalScore">
              <el-input-number v-model="examForm.totalScore" :min="1" :max="1000"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="及格分" prop="passScore">
              <el-input-number v-model="examForm.passScore" :min="1" :max="examForm.totalScore"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="考试时长" prop="examDuration">
              <el-input-number v-model="examForm.examDuration" :min="1" :max="600"></el-input-number>
              <span style="margin-left: 10px;">分钟</span>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开始时间" prop="startTime">
              <el-date-picker
                v-model="examForm.startTime"
                type="datetime"
                placeholder="选择开始时间"
                format="yyyy-MM-dd HH:mm:ss"
                value-format="yyyy-MM-dd HH:mm:ss"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="结束时间" prop="endTime">
              <el-date-picker
                v-model="examForm.endTime"
                type="datetime"
                placeholder="选择结束时间"
                format="yyyy-MM-dd HH:mm:ss"
                value-format="yyyy-MM-dd HH:mm:ss"
              ></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="试卷套数" prop="paperCount">
          <el-input-number v-model="examForm.paperCount" :min="1" :max="10"></el-input-number>
          <span style="margin-left: 10px;">套（A/B/C等）</span>
        </el-form-item>

        <el-form-item label="参与班级" prop="classIds">
          <el-select
            :key="`class-selector-${classes.length}-${(examForm.classIds || []).length}`"
            v-model="examForm.classIds"
            multiple
            placeholder="请选择参与班级"
            :disabled="classes.length === 0"
            style="width: 100%"
            clearable
            filterable
            @change="onClassIdsChange"
            @visible-change="onSelectVisibleChange"
          >
            <!-- 全选选项 -->
            <el-option
              v-if="classes.length > 0"
              :key="'select-all'"
              :label="isAllSelected ? '取消全选' : '全选'"
              :value="isAllSelected ? 'deselect-all' : 'select-all'"
              style="background-color: #f5f7fa; border-bottom: 1px solid #e4e7ed;"
            >
              <span style="font-weight: bold; color: #409eff;">
                {{ isAllSelected ? '取消全选' : '全选' }} ({{ classes.length }}个班级)
              </span>
            </el-option>

            <!-- 班级选项 -->
            <el-option
              v-for="classItem in classes"
              :key="classItem.id"
              :label="classItem.className || classItem.gradeName || classItem.name"
              :value="classItem.id"
            >
              <span style="float: left">{{ classItem.className || classItem.gradeName || classItem.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">ID: {{ classItem.id }}</span>
            </el-option>
          </el-select>

          <!-- 已选班级显示 -->
          <div v-if="selectedClassNames.length > 0" style="margin-top: 8px;">
            <div style="display: flex; flex-wrap: wrap; gap: 4px;">
              <el-tag
                v-for="className in selectedClassNames"
                :key="className"
                size="small"
                type="success"
                closable
                @close="removeClassByName(className)"
              >
                {{ className }}
              </el-tag>
            </div>
          </div>

          <div v-if="classes.length === 0" style="color: #f56c6c; font-size: 12px; margin-top: 5px;">
            暂无可用班级，请先在课程管理中关联班级
          </div>
          <div v-else style="color: #909399; font-size: 12px; margin-top: 5px;">
            共 {{ classes.length }} 个班级可选，已选择 {{ (examForm.classIds || []).length }} 个
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="examDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExamForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 试卷编辑对话框 -->
    <el-dialog
      title="编辑试卷"
      :visible.sync="paperDialogVisible"
      width="90%"
      :before-close="closePaperDialog"
    >
      <!-- 试卷选择器 -->
      <div style="margin-bottom: 20px; padding: 15px; background-color: #f5f7fa; border-radius: 4px;">
        <div style="display: flex; align-items: center; gap: 15px; flex-wrap: wrap;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-weight: bold; color: #303133;">选择试卷：</span>
            <el-select
              v-model="currentPaperId"
              placeholder="请选择要编辑的试卷"
              style="width: 200px"
              @change="onPaperChange"
            >
              <el-option
                v-for="paper in examPapers"
                :key="paper.id"
                :label="`${paper.paperCode} - ${paper.paperName}`"
                :value="paper.id"
              >
                <span style="float: left">{{ paper.paperCode }} - {{ paper.paperName }}</span>
                <span style="float: right; color: #8492a6; font-size: 12px">
                  {{ paper.questionCount || 0 }}题
                </span>
              </el-option>
            </el-select>

            <!-- 调试信息 -->
            <div v-if="examPapers.length === 0" style="color: #f56c6c; font-size: 12px; margin-left: 10px;">
              暂无试卷数据
            </div>
            <div v-else style="color: #67c23a; font-size: 12px; margin-left: 10px;">
              共 {{ examPapers.length }} 套试卷
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 10px;">
            <el-button
              type="primary"
              size="small"
              @click="createNewPaper"
              :disabled="!currentExam || !currentExam.id"
            >
              新建试卷
            </el-button>
            <el-button
              type="success"
              size="small"
              @click="setActivePaper"
              :disabled="!currentPaperId"
            >
              设为当前使用试卷
            </el-button>
          </div>

          <div v-if="currentPaper" style="color: #606266; font-size: 14px;">
            <span v-if="currentPaper.isActive" style="color: #67c23a; font-weight: bold;">
              ✓ 当前使用试卷
            </span>
            <span v-else style="color: #909399;">
              备用试卷
            </span>
          </div>
        </div>
      </div>
      <div class="paper-editor">
        <div class="paper-header">
          <h3>{{ currentPaper.paperName }} ({{ currentPaper.paperCode }}卷)</h3>
          <div class="paper-actions">
            <el-button type="primary" @click="addQuestionToPaper">添加题目</el-button>
            <el-button type="success" @click="randomGeneratePaper" v-if="currentExam.examType === 2">随机组卷</el-button>
          </div>
        </div>

        <!-- 题目列表 -->
        <div class="question-list">
          <el-table :data="paperQuestions" border>
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="questionOrder" label="题号" width="80"></el-table-column>
            <el-table-column prop="questionType" label="题型" width="100">
              <template slot-scope="scope">
                {{ getQuestionTypeText(scope.row.questionType) }}
              </template>
            </el-table-column>
            <el-table-column prop="questionContent" label="题目内容" min-width="200">
              <template slot-scope="scope">
                <div class="question-content">
                  {{ scope.row.questionContent }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="questionScore" label="分值" width="80">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.questionScore"
                  :min="1"
                  :max="100"
                  size="mini"
                  @change="updateQuestionScore(scope.row)"
                ></el-input-number>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120">
              <template slot-scope="scope">
                <el-button type="text" @click="removeQuestionFromPaper(scope.row)">移除</el-button>
                <el-button type="text" @click="moveQuestion(scope.row, 'up')" :disabled="scope.$index === 0">上移</el-button>
                <el-button type="text" @click="moveQuestion(scope.row, 'down')" :disabled="scope.$index === paperQuestions.length - 1">下移</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 添加题目对话框 -->
        <el-dialog
          title="添加题目"
          :visible.sync="addQuestionDialogVisible"
          width="80%"
          append-to-body
        >
          <div class="question-selector">
            <div class="selector-filters">
              <el-form :inline="true">
                <el-form-item label="题库">
                  <el-select v-model="questionFilters.bankId" placeholder="请选择题库" clearable @change="onBankChange">
                    <el-option
                      v-for="bank in questionRepos"
                      :key="bank.id"
                      :label="bank.name"
                      :value="bank.id"
                    ></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="文件夹" v-if="questionFilters.bankId">
                  <el-cascader
                    v-model="questionFilters.folderPath"
                    :options="folderTreeOptions"
                    :props="cascaderProps"
                    placeholder="请选择文件夹"
                    clearable
                    @change="onFolderChange"
                    style="width: 200px;"
                  ></el-cascader>
                </el-form-item>
                <el-form-item label="题型">
                  <el-select v-model="questionFilters.questionType" placeholder="请选择题型" clearable>
                    <el-option label="选择题" :value="1"></el-option>
                    <el-option label="填空题" :value="2"></el-option>
                    <el-option label="判断题" :value="3"></el-option>
                    <el-option label="简答题" :value="4"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="难度">
                  <el-select v-model="questionFilters.difficulty" placeholder="请选择难度" clearable>
                    <el-option label="简单" :value="1"></el-option>
                    <el-option label="中等" :value="2"></el-option>
                    <el-option label="困难" :value="3"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="知识点">
                  <el-input v-model="questionFilters.knowledge" placeholder="请输入知识点" clearable></el-input>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="searchQuestions" :loading="questionLoading">搜索</el-button>
                  <el-button @click="resetQuestionFilters">重置</el-button>
                </el-form-item>
              </el-form>
            </div>

            <div class="question-table">
              <div v-if="questionRepos.length === 0" class="empty-tip">
                <i class="el-icon-folder-opened"></i>
                <p>该课程暂无题库</p>
                <p style="font-size: 12px; margin-top: 8px;">请先在题库管理中创建题库</p>
              </div>
              <div v-else-if="!questionFilters.bankId" class="empty-tip">
                <i class="el-icon-info"></i>
                <p>请先选择题库</p>
              </div>
              <div v-else-if="availableQuestions.length === 0 && !questionLoading" class="empty-tip">
                <i class="el-icon-document"></i>
                <p>当前筛选条件下没有找到题目</p>
              </div>
              <el-table
                v-else
                :data="availableQuestions"
                border
                @selection-change="handleQuestionSelection"
                ref="questionTable"
                v-loading="questionLoading"
              >
                <el-table-column type="selection" width="55"></el-table-column>
                <el-table-column prop="id" label="ID" width="60"></el-table-column>
                <el-table-column prop="quType" label="题型" width="100">
                  <template slot-scope="scope">
                    {{ getQuestionTypeText(scope.row.quType) }}
                  </template>
                </el-table-column>
                <el-table-column prop="content" label="题目内容" min-width="200">
                  <template slot-scope="scope">
                    <div class="question-content">
                      {{ scope.row.content }}
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="difficulty" label="难度" width="80">
                  <template slot-scope="scope">
                    {{ getDifficultyText(scope.row.difficulty || scope.row.level) }}
                  </template>
                </el-table-column>
                <el-table-column prop="knowledge" label="知识点" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.knowledge || '未分类' }}
                  </template>
                </el-table-column>
                <el-table-column prop="point" label="分值" width="80">
                  <template slot-scope="scope">
                    {{ scope.row.point || 10 }}
                  </template>
                </el-table-column>
                <el-table-column prop="bankName" label="题库" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.bankName || '未知题库' }}
                  </template>
                </el-table-column>
                <el-table-column prop="folderName" label="文件夹" width="120">
                  <template slot-scope="scope">
                    {{ scope.row.folderName || '根目录' }}
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="pagination">
              <el-pagination
                @size-change="handleQuestionSizeChange"
                @current-change="handleQuestionCurrentChange"
                :current-page="questionPagination.currentPage"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="questionPagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="questionPagination.total"
              ></el-pagination>
            </div>
          </div>

          <div slot="footer" class="dialog-footer">
            <el-button @click="addQuestionDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="confirmAddQuestions">确定添加</el-button>
          </div>
        </el-dialog>

        <!-- 随机组卷配置对话框 -->
        <el-dialog
          title="随机组卷配置"
          :visible.sync="randomConfigDialogVisible"
          width="600px"
          append-to-body
        >
          <el-form :model="randomConfig" label-width="120px">
            <el-form-item label="选择题数量">
              <el-input-number v-model="randomConfig.choiceCount" :min="0" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="选择题分值">
              <el-input-number v-model="randomConfig.choiceScore" :min="1" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="填空题数量">
              <el-input-number v-model="randomConfig.fillCount" :min="0" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="填空题分值">
              <el-input-number v-model="randomConfig.fillScore" :min="1" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="判断题数量">
              <el-input-number v-model="randomConfig.judgeCount" :min="0" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="判断题分值">
              <el-input-number v-model="randomConfig.judgeScore" :min="1" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="简答题数量">
              <el-input-number v-model="randomConfig.essayCount" :min="0" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="简答题分值">
              <el-input-number v-model="randomConfig.essayScore" :min="1" :max="100"></el-input-number>
            </el-form-item>
            <el-form-item label="难度分布">
              <el-checkbox-group v-model="randomConfig.difficultyLevels">
                <el-checkbox label="1">简单</el-checkbox>
                <el-checkbox label="2">中等</el-checkbox>
                <el-checkbox label="3">困难</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-form>

          <div slot="footer" class="dialog-footer">
            <el-button @click="randomConfigDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="confirmRandomGenerate">确定生成</el-button>
          </div>
        </el-dialog>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="paperDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="savePaper">保存试卷</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'
import { questionApi } from '@/api/question'
import { classPaging } from '@/api/class'

export default {
  name: 'ExamCreation',
  data() {
    return {
      loading: false,
      syncing: false,
      exams: [],
      classes: [],
      isEdit: false,
      examDialogVisible: false,
      paperDialogVisible: false,
      addQuestionDialogVisible: false,
      randomConfigDialogVisible: false,
      currentExam: {},
      currentPaper: {},
      currentPaperId: null, // 当前选中的试卷ID
      examPapers: [], // 考试的所有试卷列表
      paperQuestions: [],
      availableQuestions: [],
      selectedQuestions: [],
      questionRepos: [], // 题库列表
      questionLoading: false, // 题目加载状态
      folderTreeOptions: [], // 文件夹树选项
      cascaderProps: {
        value: 'id',
        label: 'name',
        children: 'children',
        checkStrictly: true // 允许选择任意级别的节点
      },
      questionFilters: {
        bankId: null, // 题库ID
        folderPath: [], // 文件夹路径数组
        folderId: null, // 选中的文件夹ID
        questionType: null,
        difficulty: null,
        knowledge: ''
      },
      questionPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      randomConfig: {
        choiceCount: 0,
        choiceScore: 5,
        fillCount: 0,
        fillScore: 5,
        judgeCount: 0,
        judgeScore: 5,
        essayCount: 0,
        essayScore: 10,
        difficultyLevels: ['1', '2', '3']
      },
      examForm: {
        examName: '',
        examDescription: '',
        examType: 1,
        totalScore: 100,
        passScore: 60,
        examDuration: 120,
        startTime: '',
        endTime: '',
        paperCount: 1,
        classIds: []
      },
      examRules: {
        examName: [
          { required: true, message: '请输入考试名称', trigger: 'blur' }
        ],
        examType: [
          { required: true, message: '请选择考试类型', trigger: 'change' }
        ],
        totalScore: [
          { required: true, message: '请输入总分', trigger: 'blur' }
        ],
        passScore: [
          { required: true, message: '请输入及格分', trigger: 'blur' }
        ],
        examDuration: [
          { required: true, message: '请输入考试时长', trigger: 'blur' }
        ],
        startTime: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        endTime: [
          { required: true, message: '请选择结束时间', trigger: 'change' }
        ],
        paperCount: [
          { required: true, message: '请输入试卷套数', trigger: 'blur' }
        ],
        classIds: [
          { required: true, message: '请选择参与班级', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    // 不在mounted时自动加载数据，等待父组件调用
    console.log('ExamCreation组件已挂载')
    this.loadClasses() // 班级数据可以预加载，因为不涉及考试数据
  },
  computed: {
    // 是否全选
    isAllSelected() {
      const selectedCount = (this.examForm.classIds || []).length
      return this.classes.length > 0 && selectedCount === this.classes.length
    },

    // 已选班级名称列表
    selectedClassNames() {
      if (!this.examForm.classIds || this.examForm.classIds.length === 0) {
        return []
      }

      return this.examForm.classIds.map(classId => {
        const classItem = this.classes.find(c => c.id === classId)
        return classItem ? (classItem.className || classItem.gradeName || classItem.name) : `ID:${classId}`
      })
    }
  },
  methods: {
    // 加载考试列表
    async loadExams() {
      this.loading = true
      try {
        const response = await examApi.getExamsByCourse(this.$route.params.id)
        console.log('考试列表响应:', response)
        console.log('考试数据:', response.data)
        this.exams = response.data || []

        // 调试：检查每个考试的试卷套数
        if (this.exams && this.exams.length > 0) {
          this.exams.forEach((exam, index) => {
            console.log(`考试 ${index + 1}:`, {
              id: exam.id,
              examName: exam.examName,
              paperCount: exam.paperCount,
              paperCountType: typeof exam.paperCount
            })
          })
        }
      } catch (error) {
        console.error('加载考试列表失败:', error)
        this.$message.error('加载考试列表失败')
      } finally {
        this.loading = false
      }
    },

    // 加载数据（供父组件调用）
    async loadData() {
      await this.loadExams()
    },

    // 同步试卷套数
    async syncPaperCounts() {
      this.syncing = true
      try {
        console.log('开始同步试卷套数')
        await examApi.syncAllExamPaperCounts()
        this.$message.success('试卷套数同步成功')

        // 刷新考试列表
        await this.loadExams()
      } catch (error) {
        console.error('同步试卷套数失败:', error)
        this.$message.error('同步试卷套数失败: ' + (error.message || '未知错误'))
      } finally {
        this.syncing = false
      }
    },

    // 加载班级列表
    async loadClasses() {
      console.log('=== loadClasses 开始 ===')
      console.log('courseId:', this.$route.params.id)
      try {
        // 获取课程关联的班级列表
        if (this.$route.params.id) {
          console.log('使用 getGradesByCourseId API 获取课程关联的班级')
          const { getGradesByCourseId } = await import('@/api/course')
          const res = await getGradesByCourseId(this.$route.params.id)
          console.log('getGradesByCourseId 响应:', res)
          console.log('响应状态码:', res?.code)
          console.log('响应数据:', res?.data)
          console.log('数据类型:', typeof res?.data)
          console.log('数据长度:', res?.data?.length)

          if (res && res.code === 1 && res.data && res.data.length > 0) {
            // 直接使用 CourseGradeVO 中的数据，包含 gradeId 和 gradeName
            this.classes = res.data.map(item => ({
              id: item.gradeId,
              gradeId: item.gradeId,
              grade_id: item.gradeId,
              grade_name: item.gradeName,
              gradeName: item.gradeName,
              name: item.gradeName,
              className: item.gradeName, // 用于显示
              code: item.gradeCode
            }))
            console.log('获取课程关联班级成功:', this.classes)
            console.log('班级数量:', this.classes.length)
          } else {
            console.warn('该课程没有关联的班级或获取失败:', res)
            console.log('尝试获取所有班级作为备选')

            // 如果课程没有关联班级，尝试获取所有班级
            try {
              const { getClassList } = await import('@/api/homework')
              const allClassesRes = await getClassList()
              console.log('所有班级响应:', allClassesRes)

              if (allClassesRes && allClassesRes.code === 1 && allClassesRes.data && allClassesRes.data.length > 0) {
                this.classes = allClassesRes.data.map(item => ({
                  id: item.id,
                  gradeId: item.id,
                  grade_id: item.id,
                  grade_name: item.grade_name || item.gradeName,
                  gradeName: item.grade_name || item.gradeName,
                  name: item.grade_name || item.gradeName,
                  className: item.grade_name || item.gradeName,
                  code: item.grade_code || item.gradeCode
                }))
                console.log('获取所有班级成功:', this.classes)
                this.$message.info('该课程暂未关联班级，显示所有可选班级')
              } else {
                this.classes = []
                this.$message.warning('无法获取班级列表')
              }
            } catch (fallbackError) {
              console.error('获取所有班级也失败:', fallbackError)
              this.classes = []
              this.$message.warning('该课程暂未关联任何班级，且无法获取班级列表')
            }
          }
        } else {
          console.log('没有课程ID，无法获取班级列表')
          this.classes = []
          this.$message.warning('无法获取课程信息')
        }

        console.log('=== loadClasses 完成 ===')
      } catch (error) {
        console.error('获取班级列表失败:', error)
        console.error('错误详情:', error.response)
        this.$message.error('获取班级列表失败: ' + (error.message || '未知错误'))
        this.classes = []
      }
    },

    // 显示创建考试对话框
    showCreateExamDialog() {
      this.isEdit = false
      console.log('打开创建考试对话框')
      console.log('当前班级数据:', this.classes)
      console.log('班级数量:', this.classes.length)
      if (this.classes.length === 0) {
        console.log('班级数据为空，重新加载班级列表')
        this.loadClasses()
      }
      this.examDialogVisible = true
    },

    // 编辑考试
    async editExam(exam) {
      this.isEdit = true
      this.examForm = {
        ...exam,
        classIds: exam.classIds || [] // 确保classIds始终是数组
      }

      console.log('打开编辑考试对话框')
      console.log('当前班级数据:', this.classes)
      console.log('班级数量:', this.classes.length)

      // 如果班级数据为空，先加载班级列表
      if (this.classes.length === 0) {
        console.log('班级数据为空，重新加载班级列表')
        await this.loadClasses()
      }

      // 加载考试的班级信息
      try {
        console.log('加载考试班级信息，考试ID:', exam.id)
        const response = await examApi.getExamClassIds(exam.id)
        console.log('考试班级响应:', response)
        const classIds = response.data || []
        console.log('考试班级IDs:', classIds)

        // 确保classIds是数组类型
        if (Array.isArray(classIds)) {
          this.examForm.classIds = [...classIds] // 创建新数组
        } else {
          this.examForm.classIds = []
        }

        console.log('设置表单班级IDs:', this.examForm.classIds)
        console.log('设置后的类型:', typeof this.examForm.classIds)
        console.log('设置后是否为数组:', Array.isArray(this.examForm.classIds))
      } catch (error) {
        console.error('获取考试班级信息失败:', error)
        this.examForm.classIds = []
        this.$message.warning('获取班级信息失败，请重新选择')
      }

      this.examDialogVisible = true
    },

    // 删除考试
    async deleteExam(exam) {
      try {
        await this.$confirm('确定要删除这个考试吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await examApi.deleteExam(exam.id)
        this.$message.success('删除成功')
        this.loadExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    // 归档考试
    async archiveExam(exam) {
      try {
        await this.$confirm('确定要归档这个考试吗？归档后考试将移动到归档列表中。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })

        await examApi.archiveExam(exam.id)
        this.$message.success('归档成功')
        this.loadExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('归档失败')
        }
      }
    },

    // 取消归档考试
    async unarchiveExam(exam) {
      try {
        await this.$confirm('确定要取消归档这个考试吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })

        await examApi.unarchiveExam(exam.id)
        this.$message.success('取消归档成功')
        this.loadExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('取消归档失败')
        }
      }
    },

    // 打开考试详情
    async openExamDetail(exam) {
      console.log('打开考试详情:', exam)
      this.currentExam = exam
      this.paperDialogVisible = true

      try {
        // 获取考试的试卷列表
        console.log('获取考试试卷列表，考试ID:', exam.id)
        const response = await examApi.getExamPapers(exam.id)
        console.log('试卷列表响应:', response)
        const papers = response.data || []
        console.log('试卷列表:', papers)

        // 保存所有试卷
        this.examPapers = papers

        if (papers.length > 0) {
          // 查找当前使用的试卷（isActive为true的试卷）
          const activePaper = papers.find(p => p.isActive)
          if (activePaper) {
            this.currentPaper = activePaper
            this.currentPaperId = activePaper.id
          } else {
            // 如果没有设置当前使用试卷，默认选择第一个
            this.currentPaper = papers[0]
            this.currentPaperId = papers[0].id
          }
          console.log('设置当前试卷:', this.currentPaper)
          this.loadPaperQuestions()
        } else {
          // 如果没有试卷，根据试卷套数自动创建试卷
          console.log('没有试卷，根据试卷套数自动创建试卷')
          await this.createPapersByCount(exam)
        }
      } catch (error) {
        console.error('获取考试试卷失败:', error)
        this.$message.error('获取考试试卷失败')
        this.currentPaper = {}
        this.currentPaperId = null
        this.examPapers = []
      }
    },

    // 创建默认试卷
    async createDefaultPaper(exam) {
      try {
        console.log('创建默认试卷，考试ID:', exam.id)
        const response = await examApi.createPaper(exam.id, 'A', `${exam.examName} - A卷`)
        console.log('创建试卷响应:', response)
        this.currentPaper = response.data
        console.log('设置当前试卷为创建的试卷:', this.currentPaper)
        this.$message.success('已自动创建默认试卷')
        this.loadPaperQuestions()
      } catch (error) {
        console.error('创建默认试卷失败:', error)
        this.$message.error('创建默认试卷失败')
        this.currentPaper = {}
      }
    },

    // 加载试卷题目
    async loadPaperQuestions() {
      try {
        const response = await examApi.getPaperQuestions(this.currentPaper.id)
        this.paperQuestions = response.data || []

        // 如果没有题目，显示友好提示而不是错误信息
        if (!this.paperQuestions || this.paperQuestions.length === 0) {
          this.$message.info('该试卷暂无题目，请添加题目')
        }
      } catch (error) {
        // 检查是否是404错误（试卷不存在）还是其他错误
        if (error.response && error.response.status === 404) {
          this.paperQuestions = []
          this.$message.info('该试卷暂无题目，请添加题目')
        } else {
          this.$message.error('加载试卷题目失败')
        }
      }
    },

    // 添加题目到试卷
    async addQuestionToPaper() {
      console.log('添加题目到试卷 - 当前状态检查:')
      console.log('- currentPaper:', this.currentPaper)
      console.log('- currentExam:', this.currentExam)

      if (!this.currentPaper || !this.currentPaper.id) {
        // 如果currentPaper为空，尝试重新获取或创建
        if (this.currentExam && this.currentExam.id) {
          console.log('currentPaper为空，尝试重新获取试卷列表')
          try {
            const response = await examApi.getExamPapers(this.currentExam.id)
            const papers = response.data || []
            console.log('重新获取的试卷列表:', papers)

            if (papers.length > 0) {
              this.currentPaper = papers[0]
              console.log('重新设置当前试卷:', this.currentPaper)
            } else {
              // 创建默认试卷
              console.log('没有试卷，创建默认试卷')
              const createResponse = await examApi.createPaper(this.currentExam.id, 'A', `${this.currentExam.examName} - A卷`)
              this.currentPaper = createResponse.data
              console.log('创建并设置当前试卷:', this.currentPaper)
              this.$message.success('已自动创建默认试卷')
            }
          } catch (error) {
            console.error('重新获取试卷失败:', error)
            this.$message.error('试卷信息获取失败，请重新打开考试详情')
            return
          }
        } else {
          this.$message.error('试卷信息不完整，请重新打开考试详情')
          return
        }
      }

      this.addQuestionDialogVisible = true
      this.loadQuestionRepos() // 先加载题库列表
    },

    // 加载题库列表
    async loadQuestionRepos() {
      try {
        const response = await this.getCourseQuestionRepos()
        this.questionRepos = response.data || []

        if (this.questionRepos.length === 0) {
          this.$message.warning('该课程暂无题库，请先创建题库')
          return
        }

        // 如果只有一个题库，自动选择
        if (this.questionRepos.length === 1) {
          this.questionFilters.repoId = this.questionRepos[0].id
        }

        // 加载题目列表
        this.searchQuestions()
      } catch (error) {
        console.error('加载题库列表失败:', error)
        this.questionRepos = []
        this.$message.error('加载题库列表失败')
      }
    },

    // 获取课程关联的题库列表
    async getCourseQuestionRepos() {
      try {
        const { getQuestionBanksByCourseId } = await import('@/api/questionBank')
        const response = await getQuestionBanksByCourseId(this.$route.params.id)
        return response
      } catch (error) {
        console.error('获取课程题库列表失败:', error)
        throw error
      }
    },

    // 题库选择变化
    async onBankChange(bankId) {
      this.questionFilters.folderPath = []
      this.questionFilters.folderId = null
      this.folderTreeOptions = []

      if (bankId) {
        // 加载该题库的文件夹树
        await this.loadFolderTree(bankId)
      }

      // 重置分页并搜索题目
      this.questionPagination.currentPage = 1
      this.searchQuestions()
    },

    // 文件夹选择变化
    onFolderChange(folderPath) {
      this.questionFilters.folderPath = folderPath || []
      this.questionFilters.folderId = folderPath && folderPath.length > 0 ? folderPath[folderPath.length - 1] : null

      // 重置分页并搜索题目
      this.questionPagination.currentPage = 1
      this.searchQuestions()
    },

    // 加载文件夹树
    async loadFolderTree(bankId) {
      try {
        const { getQuestionBankFolderTree } = await import('@/api/questionBank')
        const response = await getQuestionBankFolderTree(bankId)
        this.folderTreeOptions = response.data || []
      } catch (error) {
        console.error('加载文件夹树失败:', error)
        this.$message.error('加载文件夹树失败')
      }
    },

    // 获取题库名称
    getBankName(bankId) {
      const bank = this.questionRepos.find(b => b.id === bankId)
      return bank ? bank.name : '未知题库'
    },

    // 搜索题目
    async searchQuestions() {
      // 如果没有选择题库，提示用户先选择题库
      if (!this.questionFilters.bankId) {
        this.availableQuestions = []
        this.questionPagination.total = 0
        return
      }

      this.questionLoading = true
      try {
        let response
        if (this.questionFilters.folderId) {
          // 如果选择了具体文件夹，搜索该文件夹中的题目
          const { getFolderQuestions } = await import('@/api/questionBank')
          response = await getFolderQuestions(this.questionFilters.folderId)
        } else {
          // 如果只选择了题库，搜索该题库中的所有题目
          const { getBankQuestions } = await import('@/api/questionBank')
          response = await getBankQuestions(this.questionFilters.bankId)
        }

        let allQuestions = response.data || []

        // 调试信息：打印题目数据结构
        if (allQuestions.length > 0) {
          console.log('题目数据结构示例:', allQuestions[0])
          console.log('题目字段检查:')
          const firstQuestion = allQuestions[0]
          console.log('- id:', firstQuestion.id)
          console.log('- bankId:', firstQuestion.bankId)
          console.log('- folderId:', firstQuestion.folderId)
          console.log('- questionId:', firstQuestion.questionId)
          console.log('- title:', firstQuestion.title)
          console.log('- type:', firstQuestion.type)
          console.log('- difficulty:', firstQuestion.difficulty)
          console.log('- folderName:', firstQuestion.folderName)
        }

        // 转换数据格式以匹配表格显示
        allQuestions = allQuestions.map(q => ({
          id: q.id || q.questionId,
          content: q.title || '',
          quType: q.type || 1,
          knowledge: '', // 需要从题目详情获取
          point: 10, // 默认分值
          difficulty: q.difficulty || 0.5,
          answer: '', // 需要从题目详情获取
          analysis: '', // 需要从题目详情获取
          bankName: this.getBankName(q.bankId),
          folderName: q.folderName || '根目录',
          bankId: q.bankId,
          folderId: q.folderId
        }))

        // 客户端筛选（因为API可能不支持服务端筛选）
        if (this.questionFilters.questionType) {
          allQuestions = allQuestions.filter(q => q.quType === this.questionFilters.questionType)
        }
        if (this.questionFilters.knowledge) {
          allQuestions = allQuestions.filter(q =>
            q.knowledge && q.knowledge.includes(this.questionFilters.knowledge)
          )
        }
        if (this.questionFilters.difficulty) {
          allQuestions = allQuestions.filter(q => {
            const difficulty = q.difficulty || q.level
            return difficulty === this.questionFilters.difficulty
          })
        }

        // 客户端分页
        this.questionPagination.total = allQuestions.length
        const start = (this.questionPagination.currentPage - 1) * this.questionPagination.pageSize
        const end = start + this.questionPagination.pageSize
        this.availableQuestions = allQuestions.slice(start, end)

        // 如果没有找到题目，显示友好提示
        if (this.availableQuestions.length === 0 && allQuestions.length === 0) {
          this.$message.info('当前题库暂无题目')
        } else if (this.availableQuestions.length === 0 && allQuestions.length > 0) {
          this.$message.info('当前筛选条件下没有找到题目')
        }
      } catch (error) {
        console.error('搜索题目失败:', error)
        this.availableQuestions = []
        this.questionPagination.total = 0

        // 检查是否是404错误（没有题目）还是其他错误
        if (error.response && error.response.status === 404) {
          this.$message.info('当前题库暂无题目')
        } else {
          this.$message.error('搜索题目失败')
        }
      } finally {
        this.questionLoading = false
      }
    },

    // 重置题目筛选条件
    resetQuestionFilters() {
      this.questionFilters = {
        bankId: null,
        folderPath: [],
        folderId: null,
        questionType: null,
        difficulty: null,
        knowledge: ''
      }
      this.folderTreeOptions = []
      this.questionPagination.currentPage = 1
      this.availableQuestions = []
      this.questionPagination.total = 0
    },

    // 处理题目选择
    handleQuestionSelection(selection) {
      this.selectedQuestions = selection
    },

    // 确认添加题目
    async confirmAddQuestions() {
      if (this.selectedQuestions.length === 0) {
        this.$message.warning('请选择要添加的题目')
        return
      }

      // 检查currentPaper状态
      console.log('当前试卷状态:', this.currentPaper)
      console.log('当前考试状态:', this.currentExam)

      if (!this.currentPaper || !this.currentPaper.id) {
        // 如果currentPaper为空，尝试重新获取
        if (this.currentExam && this.currentExam.id) {
          console.log('currentPaper为空，尝试重新获取试卷列表')
          try {
            const response = await examApi.getExamPapers(this.currentExam.id)
            const papers = response.data || []
            console.log('重新获取的试卷列表:', papers)

            if (papers.length > 0) {
              this.currentPaper = papers[0]
              console.log('重新设置当前试卷:', this.currentPaper)
            } else {
              // 创建默认试卷
              console.log('没有试卷，创建默认试卷')
              const createResponse = await examApi.createPaper(this.currentExam.id, 'A', `${this.currentExam.examName} - A卷`)
              this.currentPaper = createResponse.data
              console.log('创建并设置当前试卷:', this.currentPaper)
            }
          } catch (error) {
            console.error('重新获取试卷失败:', error)
            this.$message.error('试卷信息获取失败，请重新打开考试详情')
            return
          }
        } else {
          this.$message.error('试卷信息不完整，请重新打开考试详情')
          return
        }
      }

      // 再次检查
      if (!this.currentPaper || !this.currentPaper.id) {
        this.$message.error('无法获取有效的试卷信息')
        return
      }

      try {
        const questionIds = this.selectedQuestions.map(q => q.id)
        console.log('准备添加题目到试卷:', this.currentPaper.id, '题目IDs:', questionIds)

        // 临时解决方案：如果currentPaper.id仍然是undefined，使用currentExam.id作为试卷ID
        let paperId = this.currentPaper.id
        if (!paperId && this.currentExam && this.currentExam.id) {
          console.log('使用考试ID作为试卷ID的临时解决方案')
          paperId = this.currentExam.id
        }

        if (!paperId) {
          this.$message.error('无法确定试卷ID')
          return
        }

        console.log('最终使用的试卷ID:', paperId)

        // 如果后端API有问题，先尝试创建试卷
        try {
          await examApi.addQuestionsToPaper(paperId, questionIds)
        } catch (apiError) {
          console.error('添加题目API调用失败:', apiError)
          // 如果API调用失败，尝试先创建试卷
          if (this.currentExam && this.currentExam.id) {
            console.log('API调用失败，尝试先创建试卷')
            try {
              const createResponse = await examApi.createPaper(this.currentExam.id, 'A', `${this.currentExam.examName} - A卷`)
              this.currentPaper = createResponse.data
              console.log('创建试卷成功，重新尝试添加题目')
              await examApi.addQuestionsToPaper(this.currentPaper.id, questionIds)
            } catch (createError) {
              console.error('创建试卷也失败:', createError)
              throw createError
            }
          } else {
            throw apiError
          }
        }
        this.$message.success(`成功添加 ${this.selectedQuestions.length} 道题目`)
        this.addQuestionDialogVisible = false
        this.selectedQuestions = []
        this.loadPaperQuestions()
      } catch (error) {
        console.error('添加题目失败:', error)
        this.$message.error('添加题目失败')
      }
    },

    // 随机组卷
    randomGeneratePaper() {
      this.randomConfigDialogVisible = true
    },

    // 确认随机生成
    async confirmRandomGenerate() {
      try {
        await examApi.randomGeneratePaper(this.currentPaper.id, this.randomConfig)
        this.$message.success('随机组卷成功')
        this.randomConfigDialogVisible = false
        this.loadPaperQuestions()
      } catch (error) {
        this.$message.error('随机组卷失败')
      }
    },

    // 移除题目
    async removeQuestionFromPaper(question) {
      try {
        await this.$confirm('确定要移除这道题目吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await examApi.removeQuestionFromPaper(this.currentPaper.id, question.questionId)
        this.$message.success('移除成功')
        this.loadPaperQuestions()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('移除失败')
        }
      }
    },

    // 移动题目
    async moveQuestion(question, direction) {
      try {
        await examApi.moveQuestion(this.currentPaper.id, question.questionId, direction)
        this.$message.success('移动成功')
        this.loadPaperQuestions()
      } catch (error) {
        this.$message.error('移动失败')
      }
    },

    // 更新题目分值
    async updateQuestionScore(question) {
      try {
        await examApi.updateQuestionScore(this.currentPaper.id, question.questionId, question.questionScore)
        this.$message.success('更新分值成功')
      } catch (error) {
        this.$message.error('更新分值失败')
      }
    },

    // 保存试卷
    async savePaper() {
      try {
        await examApi.savePaper(this.currentPaper.id, this.paperQuestions)
        this.$message.success('保存试卷成功')
        this.paperDialogVisible = false
      } catch (error) {
        this.$message.error('保存试卷失败')
      }
    },

    // 关闭试卷对话框
    closePaperDialog() {
      this.paperDialogVisible = false
      this.currentExam = {}
      this.currentPaper = {}
      this.currentPaperId = null
      this.examPapers = []
      this.paperQuestions = []
    },

    // 试卷选择变化
    onPaperChange(paperId) {
      console.log('试卷选择变化:', paperId)
      const selectedPaper = this.examPapers.find(p => p.id === paperId)
      if (selectedPaper) {
        this.currentPaper = selectedPaper
        this.loadPaperQuestions()
        console.log('切换到试卷:', selectedPaper)
      }
    },

    // 根据试卷套数自动创建试卷
    async createPapersByCount(exam) {
      try {
        const paperCount = exam.paperCount || 1
        console.log('根据试卷套数创建试卷:', paperCount)

        const createdPapers = []
        const failedPapers = []

        // 创建指定数量的试卷
        for (let i = 0; i < paperCount; i++) {
          const paperCode = String.fromCharCode(65 + i) // A, B, C, D...
          const paperName = `${exam.examName} - ${paperCode}卷`

          console.log(`创建试卷 ${i + 1}/${paperCount}:`, paperCode, paperName)
          try {
            const response = await examApi.createPaper(exam.id, paperCode, paperName)
            console.log('创建试卷响应:', response)

            // 检查响应是否成功
            if (response && response.code === 1 && response.data) {
              const newPaper = response.data
              createdPapers.push(newPaper)
              console.log(`试卷 ${paperCode} 创建成功:`, newPaper)
            } else {
              console.error(`试卷 ${paperCode} 创建失败:`, response)
              failedPapers.push(paperCode)
            }
          } catch (paperError) {
            console.error(`创建试卷 ${paperCode} 时发生错误:`, paperError)
            failedPapers.push(paperCode)
          }
        }

        // 根据创建结果处理
        if (createdPapers.length > 0) {
          // 设置试卷列表
          this.examPapers = createdPapers

          // 默认选择第一套试卷（A卷）
          this.currentPaper = createdPapers[0]
          this.currentPaperId = createdPapers[0].id

          console.log('成功创建的试卷:', createdPapers)

          if (failedPapers.length > 0) {
            this.$message.warning(`部分试卷创建成功：${createdPapers.map(p => p.paperCode).join('、')}卷，失败：${failedPapers.join('、')}卷`)
          } else {
            this.$message.success(`已自动创建 ${createdPapers.length} 套试卷：${createdPapers.map(p => p.paperCode).join('、')}卷`)
          }

          this.loadPaperQuestions()
        } else {
          // 所有试卷创建都失败
          console.error('所有试卷创建都失败')
          this.$message.error('试卷创建失败，请检查后端服务或手动创建试卷')
          this.currentPaper = {}
          this.currentPaperId = null
          this.examPapers = []
        }
      } catch (error) {
        console.error('批量创建试卷失败:', error)
        this.$message.error('批量创建试卷失败')
        this.currentPaper = {}
        this.currentPaperId = null
        this.examPapers = []
      }
    },

    // 创建新试卷
    async createNewPaper() {
      if (!this.currentExam || !this.currentExam.id) {
        this.$message.error('请先选择考试')
        return
      }

      try {
        // 生成新的试卷代码
        const existingCodes = this.examPapers.map(p => p.paperCode)
        let newCode = 'A'
        let counter = 1
        while (existingCodes.includes(newCode)) {
          newCode = String.fromCharCode(65 + counter) // A, B, C, D...
          counter++
        }

        const paperName = `${this.currentExam.examName} - ${newCode}卷`

        console.log('创建新试卷:', newCode, paperName)
        const response = await examApi.createPaper(this.currentExam.id, newCode, paperName)
        console.log('创建试卷响应:', response)

        // 添加到试卷列表
        const newPaper = response.data
        this.examPapers.push(newPaper)
        this.currentPaper = newPaper
        this.currentPaperId = newPaper.id

        this.$message.success(`已创建新试卷：${newCode}卷`)
        this.loadPaperQuestions()
      } catch (error) {
        console.error('创建新试卷失败:', error)
        this.$message.error('创建新试卷失败')
      }
    },

    // 设置当前使用试卷
    async setActivePaper() {
      if (!this.currentPaperId) {
        this.$message.error('请先选择试卷')
        return
      }

      try {
        console.log('设置试卷为当前使用试卷:', this.currentPaperId)

        // 调用后端API设置当前使用试卷
        await examApi.setActivePaper(this.currentPaperId)

        // 更新前端显示
        this.examPapers.forEach(paper => {
          paper.isActive = paper.id === this.currentPaperId
        })

        this.currentPaper.isActive = true
        this.$message.success('已设置为当前使用试卷')

        // 刷新试卷列表以获取最新状态
        if (this.currentExam && this.currentExam.id) {
          const response = await examApi.getExamPapers(this.currentExam.id)
          this.examPapers = response.data || []
        }
      } catch (error) {
        console.error('设置当前使用试卷失败:', error)
        this.$message.error('设置失败: ' + (error.message || '未知错误'))
      }
    },

    // 提交考试表单
    async submitExamForm() {
      try {
        await this.$refs.examForm.validate()

        // 准备提交数据
        const { classIds, ...examData } = this.examForm // 排除classIds字段
        const submitData = {
          ...examData,
          courseId: parseInt(this.$route.params.id), // 从路由参数获取课程ID
          teacherId: this.$store.getters.userId || 1 // 从store获取教师ID，默认为1
        }

        // 调试：检查提交数据
        console.log('提交的考试数据:', submitData)
        console.log('试卷套数:', submitData.paperCount, '类型:', typeof submitData.paperCount)

        if (this.isEdit) {
          // 更新考试基本信息
          await examApi.updateExam(submitData.id, submitData)

          // 保存班级关联
          if (classIds && classIds.length > 0) {
            console.log('保存考试班级关联:', submitData.id, classIds)
            await examApi.saveExamClasses(submitData.id, classIds)
            console.log('考试班级关联保存成功')
          }

          this.$message.success('更新考试成功')
        } else {
          // 创建考试
          const response = await examApi.createExam(submitData)
          const examId = response.data.id
          const createdExam = response.data

          // 保存班级关联
          if (classIds && classIds.length > 0) {
            console.log('保存新考试班级关联:', examId, classIds)
            await examApi.saveExamClasses(examId, classIds)
            console.log('新考试班级关联保存成功')
          }

          // 根据试卷套数自动创建试卷
          const paperCount = submitData.paperCount || 1
          if (paperCount > 1) {
            console.log('创建考试后自动创建试卷，套数:', paperCount)
            try {
              const createdPapers = []
              const failedPapers = []

              // 创建指定数量的试卷
              for (let i = 0; i < paperCount; i++) {
                const paperCode = String.fromCharCode(65 + i) // A, B, C, D...
                const paperName = `${createdExam.examName} - ${paperCode}卷`

                console.log(`自动创建试卷 ${i + 1}/${paperCount}:`, paperCode, paperName)
                try {
                  const paperResponse = await examApi.createPaper(examId, paperCode, paperName)
                  console.log('创建试卷响应:', paperResponse)

                  // 检查响应是否成功
                  if (paperResponse && paperResponse.code === 1 && paperResponse.data) {
                    createdPapers.push(paperResponse.data)
                    console.log(`试卷 ${paperCode} 创建成功`)
                  } else {
                    console.error(`试卷 ${paperCode} 创建失败:`, paperResponse)
                    failedPapers.push(paperCode)
                  }
                } catch (paperError) {
                  console.error(`创建试卷 ${paperCode} 时发生错误:`, paperError)
                  failedPapers.push(paperCode)
                }
              }

              console.log('自动创建试卷完成:', createdPapers)

              if (createdPapers.length > 0) {
                if (failedPapers.length > 0) {
                  this.$message.warning(`创建考试成功，部分试卷创建成功：${createdPapers.map(p => p.paperCode).join('、')}卷，失败：${failedPapers.join('、')}卷`)
                } else {
                  this.$message.success(`创建考试成功，已自动创建 ${createdPapers.length} 套试卷：${createdPapers.map(p => p.paperCode).join('、')}卷`)
                }
              } else {
                this.$message.warning('创建考试成功，但试卷创建失败，请手动创建试卷')
              }
            } catch (paperError) {
              console.error('自动创建试卷失败:', paperError)
              this.$message.warning('创建考试成功，但自动创建试卷失败，请手动创建试卷')
            }
          } else {
            this.$message.success('创建考试成功')
          }
        }

        this.examDialogVisible = false
        this.loadExams()
      } catch (error) {
        if (error !== false) {
          this.$message.error(this.isEdit ? '更新考试失败' : '创建考试失败')
        }
      }
    },

    // 重置考试表单
    resetExamForm() {
      this.examForm = {
        examName: '',
        examDescription: '',
        examType: 1,
        totalScore: 100,
        passScore: 60,
        examDuration: 120,
        startTime: '',
        endTime: '',
        paperCount: 1,
        classIds: []
      }
      this.$refs.examForm && this.$refs.examForm.resetFields()
    },

    // 班级选择变化处理
    onClassIdsChange(value) {
      console.log('班级选择发生变化:', value)
      console.log('选择的值类型:', typeof value)
      console.log('选择的值长度:', value ? value.length : 0)
      console.log('当前examForm.classIds:', this.examForm.classIds)

      // 处理全选/取消全选
      if (value && value.includes('select-all')) {
        // 全选
        this.selectAllClasses()
        return
      } else if (value && value.includes('deselect-all')) {
        // 取消全选
        this.deselectAllClasses()
        return
      }

      // 防止重复触发
      if (this._isChangingClassIds) {
        return
      }

      this._isChangingClassIds = true
      this.$nextTick(() => {
        this._isChangingClassIds = false
      })
    },

    // 全选所有班级
    selectAllClasses() {
      const allClassIds = this.classes.map(c => c.id)
      this.examForm.classIds = [...allClassIds]
      console.log('全选班级:', this.examForm.classIds)
    },

    // 取消全选
    deselectAllClasses() {
      this.examForm.classIds = []
      console.log('取消全选班级')
    },

    // 根据班级名称移除班级
    removeClassByName(className) {
      const classItem = this.classes.find(c =>
        (c.className || c.gradeName || c.name) === className
      )
      if (classItem) {
        const index = this.examForm.classIds.indexOf(classItem.id)
        if (index > -1) {
          this.examForm.classIds.splice(index, 1)
          console.log('移除班级:', className, '剩余:', this.examForm.classIds)
        }
      }
    },

    // 选择器显示状态变化
    onSelectVisibleChange(visible) {
      console.log('选择器显示状态变化:', visible)
      if (visible) {
        console.log('选择器打开时的状态:')
        console.log('- examForm.classIds:', this.examForm.classIds)
        console.log('- classes数量:', this.classes.length)
        console.log('- 第一个班级:', this.classes[0])
      }
    },

    // 强制刷新选择器
    forceRefreshSelector() {
      console.log('=== 强制刷新选择器 ===')
      console.log('刷新前examForm.classIds:', this.examForm.classIds)

      // 强制重新设置classIds
      const currentIds = this.examForm.classIds
      this.examForm.classIds = []

      // 使用$nextTick确保DOM更新
      this.$nextTick(() => {
        this.examForm.classIds = currentIds
        console.log('刷新后examForm.classIds:', this.examForm.classIds)
        this.$message.success('选择器已刷新')
      })
    },

    // 调试选择器
    debugSelector() {
      console.log('=== 调试选择器 ===')
      console.log('1. 数据状态:')
      console.log('   - examForm.classIds:', this.examForm.classIds)
      console.log('   - examForm.classIds类型:', typeof this.examForm.classIds)
      console.log('   - examForm.classIds是否为数组:', Array.isArray(this.examForm.classIds))
      console.log('   - classes数量:', this.classes.length)

      console.log('2. 班级数据:')
      this.classes.forEach((cls, index) => {
        console.log(`   班级${index + 1}:`, {
          id: cls.id,
          className: cls.className,
          gradeName: cls.gradeName,
          name: cls.name
        })
      })

      console.log('3. 尝试手动设置选择:')
      if (this.classes.length > 0) {
        const testId = this.classes[0].id
        console.log('   设置班级ID:', testId)
        this.examForm.classIds = [testId]
        console.log('   设置后examForm.classIds:', this.examForm.classIds)

        // 强制更新视图
        this.$forceUpdate()
        console.log('   已强制更新视图')
      }
    },

    // 测试班级选择器
    testClassSelector() {
      console.log('=== 测试班级选择器 ===')
      console.log('classes数组:', this.classes)
      console.log('classes长度:', this.classes.length)
      console.log('examForm.classIds:', this.examForm.classIds)
      console.log('examForm.classIds类型:', typeof this.examForm.classIds)
      console.log('examForm.classIds是否为数组:', Array.isArray(this.examForm.classIds))

      if (this.classes.length > 0) {
        console.log('第一个班级数据:', this.classes[0])
        console.log('第一个班级的className:', this.classes[0].className)
        console.log('第一个班级的gradeName:', this.classes[0].gradeName)
        console.log('第一个班级的name:', this.classes[0].name)
        console.log('第一个班级的id:', this.classes[0].id)
        console.log('第一个班级的id类型:', typeof this.classes[0].id)
      }

      // 尝试手动设置一个班级ID
      if (this.classes.length > 0) {
        // 确保classIds是数组
        if (!Array.isArray(this.examForm.classIds)) {
          this.examForm.classIds = []
        }
        this.examForm.classIds = [this.classes[0].id]
        console.log('手动设置班级ID:', this.examForm.classIds)
        console.log('设置后的类型:', typeof this.examForm.classIds)
        console.log('设置后是否为数组:', Array.isArray(this.examForm.classIds))
      }
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        1: 'draft',
        2: 'published',
        3: 'ongoing',
        4: 'finished',
        5: 'archived'
      }
      return statusMap[status] || 'draft'
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info', // 草稿 - 灰色
        2: 'success', // 已发布 - 绿色
        3: 'warning', // 进行中 - 橙色
        4: 'primary', // 已结束 - 蓝色
        5: 'danger' // 已归档 - 红色
      }
      return typeMap[status] || 'info'
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '草稿',
        2: '已发布',
        3: '进行中',
        4: '已结束',
        5: '已归档'
      }
      return statusMap[status] || '未知'
    },

    // 获取题目类型文本
    getQuestionTypeText(type) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[type] || '未知'
    },

    // 获取难度文本
    getDifficultyText(difficulty) {
      // 处理数字类型的难度
      if (typeof difficulty === 'number') {
        const difficultyMap = {
          1: '简单',
          2: '中等',
          3: '困难'
        }
        return difficultyMap[difficulty] || '未知'
      }

      // 处理小数类型的难度（0.1-0.3简单，0.4-0.6中等，0.7-1.0困难）
      if (typeof difficulty === 'number' && difficulty > 0 && difficulty <= 1) {
        if (difficulty <= 0.3) return '简单'
        if (difficulty <= 0.6) return '中等'
        if (difficulty <= 1.0) return '困难'
      }

      return '未知'
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString()
    },

    // 分页相关方法
    handleQuestionSizeChange(val) {
      this.questionPagination.pageSize = val
      this.searchQuestions()
    },

    handleQuestionCurrentChange(val) {
      this.questionPagination.currentPage = val
      this.searchQuestions()
    }
  }
}
</script>

<style scoped>
.exam-creation {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.exam-list {
  min-height: 400px;
}

.exam-name {
  color: #409EFF;
  cursor: pointer;
  font-weight: 500;
}

.exam-name:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.empty-tip {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-tip i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.empty-tip p {
  margin: 0;
  font-size: 14px;
}

.exam-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.exam-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.exam-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.exam-actions {
  display: flex;
  gap: 5px;
}

.exam-content {
  margin-bottom: 15px;
}

.exam-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  margin-right: 5px;
}

.exam-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.exam-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.exam-status.draft {
  background: #f4f4f5;
  color: #909399;
}

.exam-status.published {
  background: #e1f3d8;
  color: #67c23a;
}

.exam-status.ongoing {
  background: #fdf6ec;
  color: #e6a23c;
}

.exam-status.finished {
  background: #f0f9ff;
  color: #409eff;
}

.exam-status.archived {
  background: #fef0f0;
  color: #f56c6c;
}

.exam-time {
  color: #909399;
  font-size: 12px;
}

.paper-editor {
  min-height: 500px;
}

.paper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.paper-header h3 {
  margin: 0;
  color: #303133;
}

.paper-actions {
  display: flex;
  gap: 10px;
}

.question-list {
  margin-bottom: 20px;
}

.question-content {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.question-selector {
  min-height: 400px;
}

.selector-filters {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.question-table {
  margin-bottom: 20px;
}

.pagination {
  text-align: center;
}

.dialog-footer {
  text-align: right;
}
</style>
